import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { FC, useCallback, useMemo, useState, useRef } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { omit } from "@common/helpers/objects";
import { ClientViewSchema } from "@common/types/views/clientViews";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import { useClientViewStore } from "@hooks/view/useClientViewStore";
import interfaceStyles from "@components/mainInterface/MainInterface.module.scss";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import {
    ClientsFilters,
    ClientsQueryParams,
} from "@common/schemas/client.schema";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useClientsQuery } from "@api/workspace.api";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import classNames from "classnames";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { AccountantsTrainingCTA } from "./AccountantsTrainingCTA";
import { ClientsView } from "./ClientsView";
import { AddClientTrigger } from "./AddClientTrigger";
import { useHandleEnrollAction } from "./useHandleEnrolAction";
import { useBuiltInClientViews } from "./views/BuiltInClientViews";
import { clientViewConfig } from "./views/clientViewConfig";
import { useClientsFilters } from "./useClientsFilters";
import { ClientBulkSelectionContextProvider } from "./ClientBulkSelection/ClientBulkSelectionContextProvider";
import { ClientBulkActionsProvider } from "./ClientBulkActions/ClientBulkActions";
import styles from "./Clients.module.scss";

const availableFilters = [
    FilterTabs.START_DATE,
    FilterTabs.TEAM_MEMBERS,
    FilterTabs.LEGAL_TYPE,
    FilterTabs.PLANS,
    FilterTabs.WHO_PAYS,
    FilterTabs.SERVICES,
];

const PAGE_SIZE = 20;

export const ClientsPage: FC = () => {
    const canCreateClient = useCanPerformOrganizationAction(
        OrganizationAction.CREATE_CLIENT,
    );
    const { clientBulkSelection } = useTypedFlags();
    const activeOrganization = useActiveOrganization();
    const [page, setPage] = useState(1);
    const [currentSort, setCurrentSort] = useState<WorkspaceSortValue>(
        WorkspaceSortValue.NAME_ASC,
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [
        isAccountantsTrainingCTAVisible,
        setIsAccountantsTrainingCTAVisible,
    ] = useState(false);
    const tableRef = useRef<ViewTableHandle>(null);

    const onFiltersChange = useCallback(() => {
        setPage(1);
    }, []);

    const [cachedClientView] = useOrganizationUserCache(
        ORGANIZATION_USER_CACHE_KEYS.CLIENTS_PAGE_VIEW_CACHE_KEY,
        ClientViewSchema,
    );
    const { filters, handleFiltersChange, resetFilters, hasActiveFilters } =
        useClientsFilters(
            onFiltersChange,
            cachedClientView
                ? omit(cachedClientView.filters, ["search"])
                : undefined,
        );

    const params: ClientsQueryParams = useMemo(
        () => ({
            filters,
            limit: PAGE_SIZE,
            page: page - 1,
            sort: currentSort,
            organizationId: activeOrganization?.id ?? "",
        }),
        [page, currentSort, filters, activeOrganization?.id],
    );

    // Prefetch clients data for the current page
    useClientsQuery(params);

    const changeSort = useCallback(
        (sort: WorkspaceSortValue) => {
            setCurrentSort(sort);
            setPage(1);
        },
        [setCurrentSort, setPage],
    );

    const { view, setView } = useClientViewStore({
        cacheKey: ORGANIZATION_USER_CACHE_KEYS.CLIENTS_PAGE_VIEW_CACHE_KEY,
        parentFilters: filters,
    });
    const builtInViews = useBuiltInClientViews();

    const updateFilters = useCallback(
        (newFilters: ClientsFilters) => {
            handleFiltersChange(newFilters);
        },
        [handleFiltersChange],
    );

    const clientViewToPass = useMemo(
        () => ({
            ...view,
            options: {
                ...view.options,
                bulkActions: clientBulkSelection,
            },
        }),
        [view, clientBulkSelection],
    );

    const accountantsTrainingCTA = useMemo(
        () => (
            <AccountantsTrainingCTA
                onVisibilityChange={setIsAccountantsTrainingCTAVisible}
            />
        ),
        [],
    );

    const viewToolbar = useMemo(
        () => (
            <>
                {accountantsTrainingCTA}
                <ViewToolbar
                    view={clientViewToPass}
                    onViewChange={setView}
                    viewConfig={clientViewConfig}
                    builtInViews={builtInViews}
                    defaultFilters={filters}
                    baseViewCacheKey={
                        ORGANIZATION_USER_CACHE_KEYS.CLIENTS_PAGE_BASE_VIEW_ID_CACHE_KEY
                    }
                    className={classNames({
                        [styles.borderTop]: isAccountantsTrainingCTAVisible,
                    })}
                />
            </>
        ),
        [
            clientViewToPass,
            setView,
            builtInViews,
            filters,
            accountantsTrainingCTA,
            isAccountantsTrainingCTAVisible,
        ],
    );

    useSubNavigationPortalContent("clients-view-toolbar", viewToolbar);

    useHandleEnrollAction();

    const content = (
        <article className={interfaceStyles.fullHeightContent}>
            <TablePageHeader
                search={filters.search}
                onSearchChange={(val) => {
                    handleFiltersChange({ search: val ?? "" });
                }}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                isFullPage
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                {canCreateClient && <AddClientTrigger />}
            </TablePageHeader>

            <ClientsView
                ref={tableRef}
                filters={filters}
                updateFilters={updateFilters}
                resetFilters={resetFilters}
                hasActiveFilters={hasActiveFilters}
                view={clientViewToPass}
                onViewChange={setView}
                page={page}
                sort={currentSort}
                onPageChange={setPage}
                onSortChange={changeSort}
                expanded={expanded}
                onExpandedChange={setExpanded}
                recomputeTopOffsetOn={[isAccountantsTrainingCTAVisible]}
            />
        </article>
    );

    if (!clientBulkSelection) {
        return content;
    }

    return (
        <ClientBulkSelectionContextProvider>
            <ClientBulkActionsProvider>{content}</ClientBulkActionsProvider>
        </ClientBulkSelectionContextProvider>
    );
};
